import { useEffect, useState, useRef } from 'react';
import { FormInstance } from 'antd';

/**
 * Deep comparison utility
 */
const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj1 === obj2;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

/**
 * Hook to track form dirty state
 */
export const useFormDirty = (
  form: FormInstance,
  initialValues?: Record<string, any>,
  onDirtyChange?: (isDirty: boolean) => void
) => {
  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef(initialValues || {});

  useEffect(() => {
    // Update initial values ref when initialValues prop changes
    initialValuesRef.current = initialValues || {};
    checkDirtyState();
  }, [initialValues]);

  const checkDirtyState = () => {
    const currentValues = form.getFieldsValue();
    const dirty = !deepEqual(currentValues, initialValuesRef.current);

    if (dirty !== isDirty) {
      setIsDirty(dirty);
      onDirtyChange?.( dirty);
    }
  };

  const resetDirtyState = () => {
    initialValuesRef.current = form.getFieldsValue();
    setIsDirty(false);
    onDirtyChange?.(false);
  };

  return {
    isDirty,
    checkDirtyState,
    resetDirtyState,
  };
};
